import { useCallback, useEffect, useMemo } from "react";
import { BehaviorSubject, Subject } from "rxjs";
import { distinctUntilChanged, filter, tap, map } from "rxjs/operators";
import { useHistory } from "react-router";
import { useObservable } from "rxsupport";
import { showToast } from "common";
import { useApplicationRootUrl } from "common/component/ApplicationRootContext";
import { SelectedReferral } from "referral";
import { useSuggestPriorityReferral$ } from "./useSuggestPriorityReferral$";

const isHigherPrioritySuggestionEnabled = (): boolean => {
  return process.env.REACT_APP_HIGHER_PRIORITY_SUGGESTION_ENABLED === "true";
};

export enum DecisionOption {
  HigherPriority = "Higher priority client",
  Both = "Both clients",
  Current = "Current selected client",
}

export type ReferralSuggestionDecisionInput = {
  decision: DecisionOption;
  declineReason: string;
};

enum HigherPriorityPhase {
  Init,
  Suggest,
  Decide,
  SubmitOffer,
  SubmittingOffer,
  Redirect,
}

export const useHigherPrioritySuggestion$ = (referralGuid: string) => {
  // Core streams
  const confirmSuggestedReferralDecision$ = useMemo(
    () => new Subject<ReferralSuggestionDecisionInput | null>(),
    [],
  );
  const showSuggestedReferralDecisionForm$ = useMemo(
    () => new BehaviorSubject(false),
    [],
  );
  const suggestedReferralPhase$ = useMemo(
    () =>
      new BehaviorSubject<HigherPriorityPhase>(
        isHigherPrioritySuggestionEnabled()
          ? HigherPriorityPhase.Init
          : HigherPriorityPhase.SubmitOffer,
      ),
    [],
  );

  // External dependencies
  const suggestedReferralDecision = useObservable(
    confirmSuggestedReferralDecision$,
    null,
  );
  // Handle suggestion result
  const onSuggestionResult = useCallback(
    (result: { guid: string } | null) => {
      if (result) {
        suggestedReferralPhase$.next(HigherPriorityPhase.Decide);
      } else {
        suggestedReferralPhase$.next(HigherPriorityPhase.SubmitOffer);
      }
    },
    [suggestedReferralPhase$],
  );
  const { suggest, data: suggestedReferral } = useSuggestPriorityReferral$(
    referralGuid,
    onSuggestionResult,
  );
  const suggestedReferralGuid = useMemo(
    () => suggestedReferral?.guid,
    [suggestedReferral],
  );
  const history = useHistory();
  const rootUrl = useApplicationRootUrl();

  const redirectToPriorityReferral = useCallback(() => {
    if (suggestedReferral != null) {
      showToast.success("Redirecting to new offer for priority referral...");
      history.push(
        SelectedReferral.createReferralNewOfferLink(
          suggestedReferral.guid,
          rootUrl,
        ),
      );
    }
  }, [history, rootUrl, suggestedReferral]);

  // Stream-based phase transitions
  const phaseTransitions$ = useMemo(
    () =>
      suggestedReferralPhase$.pipe(
        distinctUntilChanged(),
        filter((phase) => phase !== HigherPriorityPhase.Init),
      ),
    [suggestedReferralPhase$],
  );

  useEffect(() => {
    if (!isHigherPrioritySuggestionEnabled()) {
      return;
    }

    // Combined stream for all phase actions
    const sub1 = phaseTransitions$
      .pipe(
        tap((phase) => {
          switch (phase) {
            case HigherPriorityPhase.Suggest:
              suggest();
              break;
            case HigherPriorityPhase.Decide:
              showSuggestedReferralDecisionForm$.next(true);
              break;
            case HigherPriorityPhase.Redirect:
              redirectToPriorityReferral();
              break;
            default:
              // SubmitOffer and SubmittingOffer phases are no-ops
              break;
          }
        }),
      )
      .subscribe();

    // User decision stream
    const sub2 = confirmSuggestedReferralDecision$.subscribe((decision) => {
      if (decision?.decision != null) {
        suggestedReferralPhase$.next(HigherPriorityPhase.SubmitOffer);
      } else {
        suggestedReferralPhase$.next(HigherPriorityPhase.Init);
      }
    });

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [
    phaseTransitions$,
    showSuggestedReferralDecisionForm$,
    redirectToPriorityReferral,
    confirmSuggestedReferralDecision$,
    suggestedReferralPhase$,
    suggest,
  ]);

  // Public API for form validation trigger
  const triggerSuggestionFromValidation = useCallback(() => {
    if (!isHigherPrioritySuggestionEnabled()) {
      return;
    }

    suggestedReferralPhase$.next(HigherPriorityPhase.Suggest);
  }, [suggestedReferralPhase$]);

  // Public API for offer submission check - stable observable
  const canSubmitOffer$ = useMemo(() => {
    // Feature disabled - always allow submission
    if (!isHigherPrioritySuggestionEnabled()) {
      return new BehaviorSubject(true);
    }

    return suggestedReferralPhase$.pipe(
      map((phase) => phase === HigherPriorityPhase.SubmitOffer),
      distinctUntilChanged(),
    );
  }, [suggestedReferralPhase$]);

  // Public API for handling offer submission result
  const handleOfferSubmitted = useCallback(
    (decision: ReferralSuggestionDecisionInput | null) => {
      // Feature disabled - always continue normal flow
      if (!isHigherPrioritySuggestionEnabled()) {
        return false;
      }

      // Only redirect if we found a suggested referral AND user chose to switch
      if (suggestedReferral && decision?.decision !== DecisionOption.Current) {
        suggestedReferralPhase$.next(HigherPriorityPhase.Redirect);
        return true; // Indicates redirect will happen
      }
      return false; // Indicates normal flow should continue
    },
    [suggestedReferralPhase$, suggestedReferral],
  );

  // Public API for submission operation
  const prepareForSubmission = useCallback(() => {
    // Feature disabled - always allow submission
    if (!isHigherPrioritySuggestionEnabled()) {
      return true;
    }

    if (
      suggestedReferralPhase$.getValue() !== HigherPriorityPhase.SubmitOffer
    ) {
      return false;
    }

    if (suggestedReferralDecision?.decision === DecisionOption.HigherPriority) {
      suggestedReferralPhase$.next(HigherPriorityPhase.Redirect);
      return false;
    } else {
      suggestedReferralPhase$.next(HigherPriorityPhase.SubmittingOffer);
      return true;
    }
  }, [suggestedReferralPhase$, suggestedReferralDecision]);

  return {
    // Observables for UI
    showSuggestedReferralDecisionForm$,
    confirmSuggestedReferralDecision$,

    // Computed values
    suggestedReferralDecision,
    suggestedReferralGuid,
    isEnabled: isHigherPrioritySuggestionEnabled(),

    // Actions
    triggerSuggestionFromValidation,
    canSubmitOffer$,
    handleOfferSubmitted,
    prepareForSubmission,

    // Phase observable for submitSignal$ integration
    suggestedReferralPhase$,
  };
};
